import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ImageIcon, Lightbulb, Loader2 } from "lucide-react";
import { toast } from "@/lib/appToast";
import { aiStockErrorIsRecoverable, aiStockUserMessage } from "@/lib/aiStockUserMessage";
import { aiStockDevTime, optimizeAiStockImage } from "@/lib/aiStockOptimizeImage";
import { api, type AiStockAnalyzedItem } from "@/lib/api";
import {
  attachStreamToVideo,
  cameraApiSupported,
  startRearCamera,
  stopMediaStream,
} from "@/lib/deviceCamera";
const ACCEPT = "image/*";

function drawVideoCover(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  canvasW: number,
  canvasH: number,
) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return;
  const scale = Math.max(canvasW / vw, canvasH / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  ctx.drawImage(video, (canvasW - dw) / 2, (canvasH - dh) / 2, dw, dh);
}

function validateImageFile(file: File): string | null {
  const ok = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
  if (!ok) return "Formato inválido. Use JPG, PNG ou WEBP.";
  if (file.size > 12 * 1024 * 1024) return "Imagem muito grande (máx. 12 MB).";
  if (file.size === 0) return "Arquivo vazio.";
  return null;
}

type Props = {
  mode: "in-app" | "token";
  token?: string;
  onBack?: () => void;
  onResult: (itens: AiStockAnalyzedItem[]) => void;
};

/**
 * Captura mobile: câmera traseira + anexar imagem.
 * `in-app` = dentro do ERP autenticado; `token` = página do QR sem login.
 */
export function AiStockCameraCapture({ mode, token, onBack, onResult }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startGenRef = useRef(0);
  const rafRef = useRef(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraDenied, setCameraDenied] = useState(false);
  const [processing, setProcessing] = useState(false);
  const processingLock = useRef(false);

  const stopPreviewLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
  }, []);

  const releaseStream = useCallback(() => {
    stopPreviewLoop();
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stopPreviewLoop]);

  const stopCamera = useCallback(() => {
    startGenRef.current += 1;
    releaseStream();
    setCameraReady(false);
  }, [releaseStream]);

  const startCamera = useCallback(async () => {
    const gen = ++startGenRef.current;
    releaseStream();
    setCameraReady(false);
    setCameraDenied(false);

    if (!cameraApiSupported()) {
      if (gen === startGenRef.current) {
        setCameraDenied(true);
      }
      return;
    }

    try {
      const stream = await startRearCamera();
      if (gen !== startGenRef.current) {
        stopMediaStream(stream);
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        stopMediaStream(stream);
        streamRef.current = null;
        if (gen === startGenRef.current) {
          setCameraDenied(true);
          setCameraReady(false);
        }
        return;
      }
      await attachStreamToVideo(video, stream);
      if (gen !== startGenRef.current) {
        stopMediaStream(stream);
        if (streamRef.current === stream) {
          streamRef.current = null;
        }
        return;
      }
      setCameraReady(true);
      setCameraDenied(false);
    } catch {
      if (gen !== startGenRef.current) return;
      releaseStream();
      setCameraDenied(true);
      setCameraReady(false);
    }
  }, [releaseStream]);

  useEffect(() => {
    void startCamera();

    const onPageHide = () => {
      startGenRef.current += 1;
      releaseStream();
    };
    window.addEventListener("pagehide", onPageHide);

    return () => {
      startGenRef.current += 1;
      window.removeEventListener("pagehide", onPageHide);
      releaseStream();
    };
    // startCamera is stable enough via refs; mount-only init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cameraReady) {
      stopPreviewLoop();
      return;
    }
    const draw = () => {
      const video = videoRef.current;
      const canvas = previewCanvasRef.current;
      const frame = frameRef.current;
      if (video && canvas && frame && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        const dpr = window.devicePixelRatio || 1;
        const w = frame.clientWidth;
        const h = frame.clientHeight;
        if (w > 0 && h > 0) {
          const pw = Math.max(1, Math.round(w * dpr));
          const ph = Math.max(1, Math.round(h * dpr));
          if (canvas.width !== pw || canvas.height !== ph) {
            canvas.width = pw;
            canvas.height = ph;
          }
          const ctx = canvas.getContext("2d");
          if (ctx) {
            drawVideoCover(ctx, video, pw, ph);
          }
        }
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => stopPreviewLoop();
  }, [cameraReady, stopPreviewLoop]);

  const processFile = useCallback(
    async (file: File) => {
      const invalid = validateImageFile(file);
      if (invalid) {
        toast.error(invalid);
        return;
      }
      if (processingLock.current) return;
      processingLock.current = true;
      setProcessing(true);
      const tAll = performance.now();
      try {
        stopCamera();

        const tOpt = performance.now();
        const optimized = await optimizeAiStockImage(file);
        aiStockDevTime("captura→otimização", tOpt, {
          inBytes: file.size,
          outBytes: optimized.size,
        });

        const tUpload = performance.now();
        if (mode === "token") {
          if (!token) throw new Error("Sessão inválida.");
          const res = await api.aiStockMobileUpload(token, optimized);
          aiStockDevTime("otimização→upload+backend+IA", tUpload, { mode: "token" });
          if (res.itens && res.itens.length > 0) {
            onResult(res.itens);
          } else {
            toast.success("Imagem enviada. Continue no computador.");
            onResult([]);
          }
        } else {
          const res = await api.aiStockAnalyzeImage(optimized);
          aiStockDevTime("otimização→upload+backend+IA", tUpload, { mode: "in-app" });
          onResult(res.itens);
        }
        aiStockDevTime("total captura→resultado", tAll, { mode });
        // Mantém processing=true até o pai desmontar (evita flash de “Abrindo câmera…”).
      } catch (e) {
        toast.error(aiStockUserMessage(e));
        setProcessing(false);
        if (aiStockErrorIsRecoverable(e)) {
          void startCamera();
        }
      } finally {
        processingLock.current = false;
      }
    },
    [mode, token, onResult, stopCamera, startCamera],
  );

  const takePhoto = useCallback(async () => {
    if (processingLock.current || !videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      toast.error("Aguarde a câmera iniciar para fotografar.");
      return;
    }
    const tCap = performance.now();
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) {
      toast.error("Não foi possível capturar a foto.");
      return;
    }
    aiStockDevTime("shutter→blob", tCap, { bytes: blob.size, w, h });
    await processFile(new File([blob], "nota.jpg", { type: "image/jpeg" }));
  }, [processFile]);

  const isQrPage = mode === "token";
  const showCameraOpening = !processing && !cameraReady && !cameraDenied;
  const showCameraDenied = !processing && !cameraReady && cameraDenied;

  return (
    <div
      className={`flex min-h-[100dvh] flex-col bg-white text-foreground ${
        isQrPage
          ? "px-5 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
          : ""
      }`}
    >
      {isQrPage ? (
        <h1 className="shrink-0 pb-3 pt-3 text-center text-base font-semibold text-gray-900">
          Ler produtos da imagem
        </h1>
      ) : (
        <header className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-3 py-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              disabled={processing}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white disabled:opacity-50"
              aria-label="Voltar"
            >
              <ChevronLeft className="h-6 w-6" strokeWidth={2.25} aria-hidden />
            </button>
          ) : (
            <span className="w-9" />
          )}
          <h1 className="flex-1 text-center text-base font-semibold">Ler produtos da imagem</h1>
          <span className="w-9" />
        </header>
      )}

      <div className={`flex min-h-0 flex-1 flex-col ${isQrPage ? "pb-1 pt-1" : "px-4 pb-4 pt-3"}`}>
        <div ref={frameRef} className="relative min-h-0 flex-1 overflow-hidden rounded-2xl bg-black">
          <video
            ref={videoRef}
            className="pointer-events-none fixed left-[-10000px] top-0 h-40 w-40 opacity-0"
            playsInline
            muted
            autoPlay
            controls={false}
            disablePictureInPicture
            disableRemotePlayback
            controlsList="nodownload nofullscreen noremoteplayback"
            aria-hidden
            tabIndex={-1}
          />
          {cameraReady && !processing ? (
            <canvas ref={previewCanvasRef} className="absolute inset-0 h-full w-full" />
          ) : null}
          {showCameraOpening ? (
            <div className="absolute inset-0 z-[1] flex min-h-[280px] items-center justify-center bg-black px-6 text-center text-sm text-white/80">
              Abrindo câmera…
            </div>
          ) : null}
          {showCameraDenied ? (
            <div className="absolute inset-0 z-[1] flex min-h-[280px] items-center justify-center bg-black px-6 text-center text-sm text-white/80">
              Não foi possível acessar a câmera. Use Anexar imagem.
            </div>
          ) : null}
          {!processing ? (
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute left-4 top-4 h-10 w-10 border-l-2 border-t-2 border-white" />
              <div className="absolute right-4 top-4 h-10 w-10 border-r-2 border-t-2 border-white" />
              <div className="absolute bottom-4 left-4 h-10 w-10 border-b-2 border-l-2 border-white" />
              <div className="absolute bottom-4 right-4 h-10 w-10 border-b-2 border-r-2 border-white" />
              <div className="absolute left-1/2 top-4 max-w-[90%] -translate-x-1/2 rounded-full bg-black/55 px-3 py-1.5 text-center text-xs text-white">
                Posicione a nota dentro da área
              </div>
              <div className="absolute bottom-4 left-1/2 flex max-w-[92%] -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs text-white">
                <Lightbulb className="h-3.5 w-3.5 shrink-0" />
                Boa iluminação e fundo neutro melhoram a leitura.
              </div>
            </div>
          ) : null}
          {processing ? (
            <div
              className="absolute inset-0 z-[2] flex items-center justify-center bg-black"
              aria-busy="true"
              aria-label="Processando imagem"
            >
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          ) : null}
        </div>

        <div className="mt-4 flex items-center justify-between px-2">
          <button
            type="button"
            disabled={processing}
            onClick={() => fileRef.current?.click()}
            className="flex w-20 flex-col items-center gap-1 text-xs font-medium text-foreground disabled:opacity-50"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 bg-white">
              <ImageIcon className="h-5 w-5" />
            </span>
            Anexar imagem
          </button>
          <button
            type="button"
            disabled={processing || !cameraReady}
            onClick={() => void takePhoto()}
            className="h-16 w-16 rounded-full border-4 border-white bg-black shadow-md disabled:opacity-40"
            aria-label="Fotografar"
          />
          <span className="w-20" aria-hidden />
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          e.target.value = "";
          if (f) void processFile(f);
        }}
      />
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
